/**
 * Per-user dashboard app order, stored only in this browser.
 * Unknown or newly assigned apps keep the server order and append after saved ids.
 */

const STORAGE_PREFIX = "dashboard:appOrder:";

export const dashboardAppOrderKey = (userId: number): string =>
  `${STORAGE_PREFIX}${userId}`;

export const readAppOrder = (userId: number): number[] => {
  try {
    const raw = localStorage.getItem(dashboardAppOrderKey(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (id): id is number => typeof id === "number" && Number.isInteger(id),
    );
  } catch {
    return [];
  }
};

export const writeAppOrder = (userId: number, ids: number[]): void => {
  try {
    localStorage.setItem(dashboardAppOrderKey(userId), JSON.stringify(ids));
  } catch {
    // Private mode or a full quota should not break the dashboard.
  }
};

export const sortAppsByOrder = <T extends { id: number }>(
  apps: T[],
  orderIds: number[],
): T[] => {
  if (orderIds.length === 0) return apps;

  const byId = new Map(apps.map((app) => [app.id, app]));
  const sorted: T[] = [];
  const seen = new Set<number>();

  for (const id of orderIds) {
    const app = byId.get(id);
    if (!app || seen.has(id)) continue;
    sorted.push(app);
    seen.add(id);
  }

  for (const app of apps) {
    if (!seen.has(app.id)) sorted.push(app);
  }

  return sorted;
};

/**
 * Move activeId onto overId inside the visible subset, then write that
 * sequence back into the same slots in the full list. Apps that are hidden
 * (for example non-favorites) stay where they are.
 */
export const reorderVisibleApps = (
  fullIds: number[],
  visibleIds: number[],
  activeId: number,
  overId: number,
): number[] => {
  if (activeId === overId) return fullIds;

  const from = visibleIds.indexOf(activeId);
  const to = visibleIds.indexOf(overId);
  if (from < 0 || to < 0) return fullIds;

  const nextVisible = visibleIds.slice();
  const [moved] = nextVisible.splice(from, 1);
  nextVisible.splice(to, 0, moved);

  const visibleSet = new Set(visibleIds);
  let cursor = 0;
  return fullIds.map((id) => {
    if (!visibleSet.has(id)) return id;
    const nextId = nextVisible[cursor];
    cursor += 1;
    return nextId;
  });
};
